import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { QRCodeSVG } from "qrcode.react";
import { FaPhone, FaEnvelope, FaGlobe, FaLinkedin, FaFacebook, FaInstagram, FaDownload, FaShareAlt, FaPrint } from "react-icons/fa";
import { generateVisitingCard, printVisitingCard } from "../utils/generateCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CardView = () => {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const cardUrl = `${window.location.origin}/card/${slug}`;

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await fetch(`${API}/cards/${slug}`);
        if (!res.ok) throw new Error("Card not found");
        const data = await res.json();
        setCard(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [slug]);

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: `${card.name} - ${card.company}`, url: cardUrl });
    } else {
      navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveContact = () => {
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${card.name}`,
      `ORG:${card.company}`,
      `TITLE:${card.title}`,
      card.phone ? `TEL:${card.phone}` : "",
      card.email ? `EMAIL:${card.email}` : "",
      card.website ? `URL:${card.website}` : "",
      "END:VCARD",
    ].filter(Boolean).join("\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${card.name.replace(/\s+/g, "_")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Card Not Found</h1>
          <p className="text-gray-400">This digital card doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="card-view">
      <Helmet>
        <title>{`${card.name} | ${card.title} - Crown Amusement`}</title>
        <meta name="description" content={`${card.name}, ${card.title} at ${card.company}. Connect with our team for premium skill gaming solutions.`} />
        <meta property="og:title" content={`${card.name} | ${card.title} - Crown Amusement`} />
        <meta property="og:description" content={`${card.name}, ${card.title} at ${card.company}. Digital business card.`} />
        <meta property="og:url" content={cardUrl} />
      </Helmet>
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="relative bg-gradient-to-b from-[#0F0F12] to-[#0A0A0D] border border-[#D4AF37]/20 rounded-3xl overflow-hidden shadow-2xl">
          {/* Gold accent line */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

          {/* Header with pattern */}
          <div className="relative h-32 bg-gradient-to-br from-[#D4AF37]/10 to-transparent overflow-hidden">
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, #D4AF37 10px, #D4AF37 11px)"
            }} />
            {/* Crown logo watermark */}
            <div className="absolute top-4 right-4 opacity-10">
              <img src="/images/logo.png" alt="" className="h-16 w-auto" />
            </div>
          </div>

          {/* Photo */}
          <div className="flex justify-center -mt-16 relative z-10">
            <div className="w-32 h-32 rounded-full border-4 border-[#D4AF37]/30 overflow-hidden bg-[#1a1a1a] shadow-lg">
              {card.photo ? (
                <img src={card.photo} alt={card.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#D4AF37] text-4xl font-bold">
                  {card.name.split(" ").map(n => n[0]).join("").toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="text-center px-8 pt-4 pb-2">
            <h1 className="text-2xl font-bold text-white tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>
              {card.name}
            </h1>
            <p className="text-[#D4AF37] text-sm mt-1 tracking-wider uppercase">{card.title}</p>
            <p className="text-gray-400 text-sm mt-1">{card.company}</p>
          </div>

          {/* Divider */}
          <div className="mx-8 my-4 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

          {/* Contact Details */}
          <div className="px-8 space-y-3 pb-6">
            {card.phone && (
              <a href={`tel:${card.phone}`} className="flex items-center gap-4 group" data-testid="card-phone">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <FaPhone className="text-[#D4AF37] text-sm" />
                </div>
                <span className="text-gray-300 group-hover:text-white transition-colors">{card.phone}</span>
              </a>
            )}
            {card.email && (
              <a href={`mailto:${card.email}`} className="flex items-center gap-4 group" data-testid="card-email">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <FaEnvelope className="text-[#D4AF37] text-sm" />
                </div>
                <span className="text-gray-300 group-hover:text-white transition-colors">{card.email}</span>
              </a>
            )}
            {card.website && (
              <a href={card.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="card-website">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <FaGlobe className="text-[#D4AF37] text-sm" />
                </div>
                <span className="text-gray-300 group-hover:text-white transition-colors">{card.website.replace(/^https?:\/\//, "")}</span>
              </a>
            )}
          </div>

          {/* Social Links */}
          {(card.linkedin || card.facebook || card.instagram) && (
            <>
              <div className="mx-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
              <div className="flex justify-center gap-4 py-5">
                {card.linkedin && (
                  <a href={card.linkedin} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-[#D4AF37]/10 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors" data-testid="card-linkedin">
                    <FaLinkedin className="text-[#D4AF37] text-lg" />
                  </a>
                )}
                {card.facebook && (
                  <a href={card.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-[#D4AF37]/10 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors" data-testid="card-facebook">
                    <FaFacebook className="text-[#D4AF37] text-lg" />
                  </a>
                )}
                {card.instagram && (
                  <a href={card.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-[#D4AF37]/10 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors" data-testid="card-instagram">
                    <FaInstagram className="text-[#D4AF37] text-lg" />
                  </a>
                )}
              </div>
            </>
          )}

          {/* QR Code */}
          <div className="mx-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
          <div className="flex justify-center py-6">
            <div className="bg-white p-3 rounded-xl" data-testid="card-qr">
              <QRCodeSVG value={cardUrl} size={120} level="M" />
            </div>
          </div>

          <div className="px-8 pb-4">
            <button
              onClick={() => printVisitingCard(card)}
              data-testid="card-print-both"
              className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#FFB81C] to-[#D4AF37] text-[#050505] font-semibold rounded-xl hover:scale-[1.02] transition-transform text-sm"
            >
              <FaPrint /> Print Visiting Card (Front & Back)
            </button>
          </div>
          <div className="px-8 pb-8 flex gap-3">
            <button
              onClick={handleSaveContact}
              data-testid="card-save-contact"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/5 border border-[#D4AF37]/20 text-[#D4AF37] font-semibold rounded-xl hover:bg-white/10 transition-colors text-sm"
            >
              <FaDownload /> Save Contact
            </button>
            <button
              onClick={handleShare}
              data-testid="card-share"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/5 border border-[#D4AF37]/20 text-[#D4AF37] font-semibold rounded-xl hover:bg-white/10 transition-colors text-sm"
            >
              <FaShareAlt /> {copied ? "Copied!" : "Share"}
            </button>
          </div>

          {/* Footer */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        </div>

        {/* Powered by */}
        <p className="text-center text-gray-600 text-xs mt-6">
          Crown Amusement Digital Card
        </p>
      </div>
    </div>
  );
};

export default CardView;
